import styled from 'styled-components/macro';
import { KeyboardEvent, useMemo, useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import {
  createFilterOptions,
  ListItemAvatar,
  ListItemIcon,
  Select,
  Typography
} from '@mui/material';
import { Search } from '@mui/icons-material';

import { HiMenuItem, HiInput, HiChip, HiBadge, HiTooltip, HiAvatar } from '..';
import { BaseReactComponent, SelectOption } from '../../../types';

// When multiple, max number of tags display
const LIMIT_TAGS = 2;

export interface HiSelectProps extends BaseReactComponent {
  /**
   * The input value. Providing an empty string will select no options. Set to an empty string `''` if you don't want any of the available options to be selected.
   * If multiple selection, provide an array.
   */
  value: string | string[] | undefined;
  /**
   *	Array of options.
   */
  options: SelectOption[];
  /**
   * Callback fired when a menu item is selected.
   *
   * @param {string | string[]} value The selected value, or array of selected values if multiple.
   *
   */
  onChange: (value: string | string[]) => void;
  /**
   * If `true`, value must be an array and the menu will support multiple selections.
   */
  multiple?: boolean;
  /**
   * If `true`, a search input is added on top of the options list.
   * @default true
   */
  searchable?: boolean;
  /**
   * The maximum number of tags that will be visible when multiple.
   * @default 2
   */
  limitTags?: number;
  /**
   * The Popper element width. By default, it takes the width of the input.
   */
  popperWidth?: string;
}

const filterOptions = createFilterOptions<SelectOption>();

/**
 * Select components are used for collecting user provided information from a **fixed list of options**.
 *
 * If your dropdown menu has got less than 6 items, consider using radio buttons instead.
 *
 * It extends Mui's `Select` component.
 */
const HiSelect = ({
  value,
  options,
  multiple,
  onChange,
  searchable = true,
  limitTags = LIMIT_TAGS,
  popperWidth,
  ...other
}: HiSelectProps) => {
  const intl = useIntl();

  const searchInputRef = useRef<HTMLInputElement>(null);
  const selectPaperRef = useRef<HTMLDivElement>(null);
  const selectInputRef = useRef<HTMLInputElement>(null);

  const [searchValue, setSearchValue] = useState<string>('');

  // Map options to key/value object to be able to request labels from id
  // Used to convert value array for Autocomplete format
  const _optionsMapping = useMemo<Record<string, SelectOption>>(() => {
    const mapping: Record<string, SelectOption> = {};
    options.forEach((o) => {
      mapping[o.id] = o;
    });
    return mapping;
  }, [options]);

  const _filteredOptions = useMemo<SelectOption[]>(() => {
    if (!searchValue.length) return options;

    return filterOptions(options, {
      inputValue: searchValue,
      getOptionLabel: (o) => o.label
    });
  }, [options, searchValue]);

  // Custom input cause onChange returning value and not event like the default Select
  const handleChange = (e: string | string[]) => {
    onChange && onChange(e);
  };

  // Handle search input change
  const handleSearchChange = (v: unknown) => {
    setSearchValue(v as string);
  };

  // When ArrowDown on search input, go to first option item
  const handleSearchInputKeyDown = (e: KeyboardEvent) => {
    if (e.code === 'ArrowDown') {
      e.preventDefault();
      if (selectPaperRef.current) {
        const firstItem = selectPaperRef.current.querySelector('li');
        setTimeout(() => {
          firstItem?.focus();
        });
      }
    }
  };

  // When ArrowUp on first option item and searchable, focus search input
  const handleOptionItemKeyDown = (index: number, e: KeyboardEvent) => {
    if (searchable && index === 0 && e.code === 'ArrowUp') {
      e.preventDefault();
      if (searchInputRef.current) {
        searchInputRef.current.focus();
      }
    }
  };

  // On Chip delete, remove it
  const handleChipDelete = (chipId: string) => {
    onChange((value as string[]).filter((v: string) => v !== chipId));

    setTimeout(() => {
      selectInputRef.current?.focus();
    });
  };

  return (
    <StyledSelect
      {...other}
      multiple={multiple}
      value={value}
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      onChange={handleChange}
      onClose={() => {
        // Reset search value. (timeout to prevent visual change on options during closing animation)
        setTimeout(() => {
          setSearchValue('');
        }, 150);
      }}
      renderValue={(selected) => {
        if (Array.isArray(selected)) {
          return (
            <>
              {(selected as string[]).slice(0, limitTags).map((v: string) => {
                return (
                  <HiChip
                    key={v}
                    className="HiSelect-tag"
                    type="removable"
                    size="small"
                    label={_optionsMapping[v].label}
                    onAction={() => handleChipDelete(v)}
                    ChipProps={{
                      // Prevent select mouseDown event to handle chip remove
                      onMouseDown: (e) => {
                        e.stopPropagation();
                      }
                    }}
                  />
                );
              })}
              {selected.length > limitTags && (
                <HiBadge
                  className="HiSelect-more-tags-badge"
                  color="info"
                  content={
                    <HiTooltip
                      title={(selected as string[])
                        .slice(2)
                        .map((v: string) => _optionsMapping[v]?.label)
                        .join(', ')}
                      sx={{ mb: '4px !important' }}
                    >
                      <span>+{selected.length - limitTags}</span>
                    </HiTooltip>
                  }
                />
              )}
            </>
          );
        } else {
          const selectedOption = _optionsMapping[selected as string];
          const IconLeftComponent = selectedOption.iconLeft;
          return (
            <>
              {selectedOption.avatar && (
                <ListItemAvatar className="HiSelect-resultLeftIcon">
                  <HiAvatar {...selectedOption.avatar} size="xsmall" />
                </ListItemAvatar>
              )}
              {!selectedOption.avatar && IconLeftComponent && (
                <ListItemIcon className="HiSelect-resultLeftIcon">
                  <IconLeftComponent fontSize="small" color="info" />
                </ListItemIcon>
              )}

              {selectedOption.label}
            </>
          );
        }
      }}
      classes={{
        select: 'HiSelect-select',
        icon: 'HiSelect-icon'
      }}
      input={<HiInput clearIcon={false} inputRef={selectInputRef} />}
      MenuProps={{
        PaperProps: {
          ref: selectPaperRef,
          style: {
            maxHeight: '40vh',
            width: popperWidth
          }
        }
      }}
    >
      {searchable && (
        <StyledSelectInputSearch
          data-testid="HiSelect-inputSearch"
          startIcon={Search}
          InputProps={{
            fullWidth: true,
            autoFocus: true,
            tabIndex: -1,
            onKeyDown: handleSearchInputKeyDown
          }}
          inputRef={searchInputRef}
          placeholder={intl.formatMessage({ id: 'HiSelect.search' })}
          onChange={handleSearchChange}
        />
      )}

      {_filteredOptions.map((option, index) => {
        return (
          <HiMenuItem
            {...option}
            key={option.id}
            value={option.id}
            checkbox={multiple}
            selected={
              Array.isArray(value)
                ? value.includes(option.id)
                : value === option.id
            }
            // Final prop is added by mui Select
            onClick={undefined}
            MenuItemProps={{
              onKeyDown: (e) => handleOptionItemKeyDown(index, e)
            }}
          />
        );
      })}

      {_filteredOptions.length === 0 && (
        <StyledNoResult>
          <Typography variant="body1" color="textSecondary">
            {intl.formatMessage({ id: 'HiAutocomplete.noOptions' })}
          </Typography>
        </StyledNoResult>
      )}
    </StyledSelect>
  );
};

const StyledSelectInputSearch = styled(HiInput)`
  position: sticky;
  top: 0;
  background-color: white;
  z-index: 10;
`;

const StyledNoResult = styled('div')`
  padding: 14px 16px 6px 16px;
`;

const StyledSelect = styled(Select)`
  padding: 0 0;

  .HiSelect-select {
    padding: 0 8px !important;
    height: 40px !important;
    display: flex;
    align-items: center;
    &:focus {
      background-color: transparent;
    }
  }

  .HiSelect-tag {
    margin: 3px;
    max-width: 85px !important;
    box-shadow: ${({ theme }) => theme.shadows[2]};
  }

  .HiSelect-resultLeftIcon {
    min-width: 28px;
  }

  .HiSelect-more-tags-badge {
    margin: 3px;
  }

  .HiSelect-icon {
    margin-right: 2px;
  }
`;

export default HiSelect;
